import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  KeyboardArrowDown as DownIcon,
  KeyboardArrowRight as RightIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/ui/PageHeader';
import StatCard from '../../components/ui/StatCard';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';
import axios from '../../utils/axios';
import { notify } from '../../utils/notify';
import {
  availableMetrics,
  distributeByShape,
  fmtInt,
  fmtMetric,
  fmtMoney,
  isMoneyMetric,
  resolveCanFinance,
} from './salesPlanMatrix';
import { ForecastMetadataChips, extractForecastMetadata } from './forecastMeta';

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

const LEVEL_LABEL = { gruppa: 'Группы', podgruppa: 'Подгруппы', article: 'Товары' };
const FORECAST_MODE_HELP = {
  simple: 'Простой: линейный расчёт от базового периода.',
  smart: 'Продвинутый: текущий сезонный прогноз.',
  auto: 'Умный: выбирает модель по правилам Forecast Lab, с откатом на продвинутый.',
};

const zeroMetrics = () => ({ qty: 0, revenue: 0, profit: 0, roi: 0, cost: 0, advSum: 0, traffic: 0 });

function scaleMetrics(m, factor) {
  const qty = (Number(m?.qty) || 0) * factor;
  const revenue = (Number(m?.revenue) || 0) * factor;
  const profit = (Number(m?.profit) || 0) * factor;
  const cost = (Number(m?.cost) || 0) * factor;
  return {
    qty, revenue, profit, cost,
    advSum: (Number(m?.advSum) || 0) * factor,
    traffic: (Number(m?.traffic) || 0) * factor,
    roi: cost > 0 ? (100 * profit / cost) : 0,
  };
}

function sumMetrics(list) {
  const acc = zeroMetrics();
  for (const m of list) {
    acc.qty += Number(m?.qty) || 0;
    acc.revenue += Number(m?.revenue) || 0;
    acc.profit += Number(m?.profit) || 0;
    acc.cost += Number(m?.cost) || 0;
    acc.advSum += Number(m?.advSum) || 0;
    acc.traffic += Number(m?.traffic) || 0;
  }
  acc.roi = acc.cost > 0 ? (100 * acc.profit / acc.cost) : 0;
  return acc;
}

export default function SalesPlanCreate() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const today = new Date();

  const [name, setName] = useState('План продаж');
  const [baseFrom, setBaseFrom] = useState(ymd(addDays(today, -30)));
  const [baseTo, setBaseTo] = useState(ymd(addDays(today, -1)));
  const [planFrom, setPlanFrom] = useState(ymd(today));
  const [planTo, setPlanTo] = useState(ymd(addDays(today, 30)));
  const [breakdownLevel, setBreakdownLevel] = useState('podgruppa');
  const [forecastMode, setForecastMode] = useState('simple');
  const [granularity, setGranularity] = useState('week');
  const [metric, setMetric] = useState('qty');
  const [filters, setFilters] = useState({ mp: '', orgMode: 'include', gruppaMode: 'include', podgruppaMode: 'include' });

  const [preview, setPreview] = useState({ rows: [], dates: [], base_days: 0, plan_days: 0, can_finance: null, forecast_metadata: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // overrides: { `${level}::${key}`: { growth_pct, manual_override, removed } }
  const [overrides, setOverrides] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [addedItems, setAddedItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const versionRef = useRef(0);

  const fetchPreview = useCallback(async () => {
    const v = ++versionRef.current;
    setLoading(true);
    try {
      const params = {
        base_from: baseFrom, base_to: baseTo,
        plan_from: planFrom, plan_to: planTo,
        breakdown_level: breakdownLevel,
        granularity,
        forecast_mode: forecastMode,
        ...buildFilterParams(filters),
      };
      const resp = await axios.get('/analytics/plans/preview', { params });
      if (v !== versionRef.current) return;
      setPreview({
        rows: resp.data?.rows || [],
        dates: resp.data?.dates || [],
        base_days: resp.data?.base_days || 0,
        plan_days: resp.data?.plan_days || 0,
        can_finance: resp.data?.can_finance ?? null,
        forecast_metadata: resp.data?.forecast_metadata || resp.data?.forecastMetadata || resp.data?.forecast_meta || null,
      });
    } catch (_) {
      if (v === versionRef.current) setPreview({ rows: [], dates: [], base_days: 0, plan_days: 0, can_finance: null, forecast_metadata: null });
    } finally {
      if (v === versionRef.current) setLoading(false);
    }
  }, [baseFrom, baseTo, planFrom, planTo, breakdownLevel, granularity, forecastMode, filters]);

  useEffect(() => {
    const t = setTimeout(fetchPreview, 350);
    return () => clearTimeout(t);
  }, [fetchPreview]);

  const canFinance = resolveCanFinance(preview.can_finance, user);
  const metrics = useMemo(() => availableMetrics(canFinance), [canFinance]);
  const dates = preview.dates;
  const forecastMeta = extractForecastMetadata(preview);
  const colCount = 2 + dates.length + 1; // Позиция + % роста + даты + Итого

  // Откат на «Штуки», если выбранная денежная метрика недоступна
  useEffect(() => {
    if (!canFinance && isMoneyMetric(metric)) setMetric('qty');
  }, [canFinance, metric]);

  const baseDays = preview.base_days;
  const planDays = preview.plan_days;

  const setOverride = (key, patch) => {
    setOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };
  const resetOverride = (key) => {
    setOverrides((prev) => {
      const next = { ...prev };
      const cur = { ...(next[key] || {}) };
      delete cur.growth_pct;
      cur.manual_override = false;
      next[key] = cur;
      return next;
    });
  };

  // Рекурсивный расчёт прогноза + каскад роста/удаления.
  const { computed, totals } = useMemo(() => {
    const comp = new Map();
    const leafForecasts = [];
    const walk = (node, inheritedGrowth, inheritedRemoved) => {
      const k = `${node.level}::${node.key}`;
      const ov = overrides[k] || {};
      const selfRemoved = inheritedRemoved || !!ov.removed;
      const effGrowth = ov.manual_override ? Number(ov.growth_pct || 0) : inheritedGrowth;
      let forecast;
      if (node.children && node.children.length) {
        const childF = node.children.map((c) => walk(c, effGrowth, selfRemoved));
        forecast = sumMetrics(childF);
      } else {
        const factor = baseDays > 0 ? (planDays / baseDays) * (1 + effGrowth / 100) : 0;
        forecast = selfRemoved ? zeroMetrics() : scaleMetrics(node.baseline?.total, factor);
        if (!selfRemoved) leafForecasts.push(forecast);
      }
      comp.set(k, { effGrowth, selfRemoved, manual: !!ov.manual_override, removed: !!ov.removed, forecast });
      return forecast;
    };
    for (const r of preview.rows) walk(r, 0, false);
    // прогноз по добавленным вручную позициям
    const addedF = addedItems.map((ai) => {
      const qty = Number(ai.plan_qty) || 0;
      const revenue = qty * (Number(ai.avg_price) || 0);
      const profit = qty * (Number(ai.unit_profit) || 0);
      return { qty, revenue, profit, cost: 0, advSum: 0, traffic: 0, roi: 0 };
    });
    return { computed: comp, totals: sumMetrics([...leafForecasts, ...addedF]) };
  }, [preview.rows, overrides, baseDays, planDays, addedItems]);

  const toggleExpand = (rowId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
      return next;
    });
  };

  // Равномерное распределение по бакетам дат (для добавленных вручную позиций).
  const uniformByDate = (total) => {
    const out = {};
    if (dates.length > 0) {
      const per = (Number(total) || 0) / dates.length;
      for (const d of dates) out[d.key] = per;
    }
    return out;
  };

  const renderRows = (nodes, depth, parentRowId) => {
    const out = [];
    for (const node of nodes) {
      const k = `${node.level}::${node.key}`;
      const rowId = `${parentRowId}/${node.key}`;
      const c = computed.get(k) || { effGrowth: 0, selfRemoved: false, manual: false, removed: false, forecast: zeroMetrics() };
      const hasChildren = node.children && node.children.length > 0;
      const isOpen = expanded.has(rowId);
      const removed = c.selfRemoved;
      // Распределение прогноза по бакетам: форма из preview.series.plan, масштаб — локальный итог.
      const byDate = distributeByShape(c.forecast[metric], node.series?.plan, dates, metric);
      out.push(
        <TableRow key={rowId} hover sx={{ opacity: removed ? 0.45 : 1 }}>
          <TableCell sx={{ pl: 1 + depth * 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {hasChildren ? (
                <IconButton size="small" onClick={() => toggleExpand(rowId)} sx={{ p: 0.25 }}>
                  {isOpen ? <DownIcon fontSize="small" /> : <RightIcon fontSize="small" />}
                </IconButton>
              ) : <Box sx={{ width: 28 }} />}
              <Typography
                variant="body2"
                sx={{ fontWeight: node.level === 'gruppa' ? 600 : node.level === 'podgruppa' ? 500 : 400, textDecoration: removed ? 'line-through' : 'none' }}
              >
                {node.name || node.key}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align="center" sx={{ borderLeft: (t) => `1px solid ${t.palette.outlineVariant}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <TextField
                size="small"
                type="number"
                value={c.manual ? (overrides[k]?.growth_pct ?? 0) : (c.effGrowth || 0)}
                onChange={(e) => setOverride(k, { growth_pct: e.target.value === '' ? 0 : Number(e.target.value), manual_override: true })}
                disabled={removed}
                sx={{ width: 88 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  sx: { '& input': { textAlign: 'right', py: 0.5 } },
                }}
              />
              {c.manual ? (
                <Tooltip title="Сбросить (наследовать от родителя)">
                  <IconButton size="small" onClick={() => resetOverride(k)} sx={{ p: 0.25 }}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : <Box sx={{ width: 22 }} />}
              <Tooltip title={c.removed ? 'Вернуть в план' : 'Убрать из плана'}>
                <IconButton size="small" color={c.removed ? 'default' : 'error'} onClick={() => setOverride(k, { removed: !c.removed })} sx={{ p: 0.25 }}>
                  {c.removed ? <AddIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
          {dates.map((d) => (
            <TableCell key={d.key} align="right">{fmtMetric(byDate[d.key], metric)}</TableCell>
          ))}
          <TableCell align="right" sx={{ fontWeight: 600, borderLeft: (t) => `1px solid ${t.palette.outlineVariant}` }}>
            {fmtMetric(c.forecast[metric], metric)}
          </TableCell>
        </TableRow>
      );
      if (hasChildren && isOpen) {
        out.push(...renderRows(node.children, depth + 1, rowId));
      }
    }
    return out;
  };

  const handleSave = async () => {
    if (!name.trim()) { notify('Укажите название плана', { severity: 'warning' }); return; }
    if (!planFrom || !planTo) { notify('Укажите период прогноза', { severity: 'warning' }); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        status: 'active',
        base_from: baseFrom,
        base_to: baseTo,
        plan_from: planFrom,
        plan_to: planTo,
        granularity,
        breakdown_level: breakdownLevel,
        forecast_mode: forecastMode,
        filters: buildFilterParams(filters),
        overrides,
        added_items: addedItems.map((ai) => ({
          level: ai.level,
          entity_key: ai.entity_key,
          parent_key: ai.parent_key || null,
          manual_plan_qty: Number(ai.plan_qty) || 0,
          avg_price: Number(ai.avg_price) || 0,
          unit_profit: Number(ai.unit_profit) || 0,
        })),
      };
      const resp = await axios.post('/analytics/plans', body);
      const id = resp.data?.id;
      notify('План сохранён', { severity: 'success' });
      if (id) navigate(`/analytics/plans/${id}`);
      else navigate('/analytics/plans');
    } catch (_) {
      /* ошибка показана интерсептором */
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Новый план продаж"
        subtitle="Базовый период задаёт факт прошлых продаж, прогноз пересчитывается с учётом % роста"
        breadcrumbs={[{ label: 'Планы продаж', path: '/analytics/plans' }, { label: 'Новый план' }]}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button onClick={() => navigate('/analytics/plans')}>Отмена</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
              Сохранить
            </Button>
          </Stack>
        )}
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField label="Название плана" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Метод прогноза:</Typography>
              <ToggleButtonGroup size="small" exclusive value={forecastMode} onChange={(_, v) => v && setForecastMode(v)}>
                <ToggleButton value="simple">Простой</ToggleButton>
                <ToggleButton value="smart">Продвинутый</ToggleButton>
                <ToggleButton value="auto">Умный</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          {forecastMode === 'simple' && (
            <Grid item xs={12} sm={6} md={4}>
              <DateRangeField label="Базовый период (факт)" valueFrom={baseFrom} valueTo={baseTo}
                onChange={({ from, to }) => { setBaseFrom(from); setBaseTo(to); }} />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <DateRangeField label="Период прогноза" valueFrom={planFrom} valueTo={planTo}
              onChange={({ from, to }) => { setPlanFrom(from); setPlanTo(to); }} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Уровень:</Typography>
              <ToggleButtonGroup size="small" exclusive value={breakdownLevel} onChange={(_, v) => v && setBreakdownLevel(v)}>
                <ToggleButton value="gruppa">{LEVEL_LABEL.gruppa}</ToggleButton>
                <ToggleButton value="podgruppa">{LEVEL_LABEL.podgruppa}</ToggleButton>
                <ToggleButton value="article">{LEVEL_LABEL.article}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            <ProductFilters value={filters} onChange={setFilters} />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {forecastMode === 'simple'
            ? `${FORECAST_MODE_HELP.simple} Базовый период: ${baseDays} дн. → Период прогноза: ${planDays} дн. Прогноз = факт × (${planDays}/${baseDays}) × (1 + % роста).`
            : `${FORECAST_MODE_HELP[forecastMode]} Период прогноза: ${planDays} дн. Прогноз = источник прогноза × (1 + % роста).`}
        </Typography>
        {forecastMeta && (
          <Box sx={{ mt: 1 }}>
            <ForecastMetadataChips metadata={forecastMeta} />
          </Box>
        )}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={canFinance ? 4 : 12}><StatCard title="План, шт" value={fmtInt(totals.qty)} color="primary" /></Grid>
        {canFinance && (
          <>
            <Grid item xs={12} sm={4}><StatCard title="План, сумма" value={fmtMoney(totals.revenue)} color="info" /></Grid>
            <Grid item xs={12} sm={4}><StatCard title="План, профит" value={fmtMoney(totals.profit)} color="success" /></Grid>
          </>
        )}
      </Grid>

      <Paper elevation={0} sx={{ border: (t) => `1px solid ${t.palette.outlineVariant}`, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2, py: 1.5, borderBottom: (t) => `1px solid ${t.palette.outlineVariant}` }}>
          <Typography variant="subtitle1" fontWeight={600}>Позиции плана (прогноз по датам)</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
              {metrics.map((m) => <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={granularity} onChange={(_, v) => v && setGranularity(v)}>
              <ToggleButton value="day">День</ToggleButton>
              <ToggleButton value="week">Неделя</ToggleButton>
              <ToggleButton value="month">Месяц</ToggleButton>
            </ToggleButtonGroup>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Добавить позицию</Button>
          </Stack>
        </Box>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Позиция</TableCell>
                <TableCell align="center" sx={{ borderLeft: (t) => `1px solid ${t.palette.outlineVariant}`, whiteSpace: 'nowrap' }}>% роста</TableCell>
                {dates.map((d) => (
                  <TableCell key={d.key} align="right" sx={{ whiteSpace: 'nowrap' }}>{d.label}</TableCell>
                ))}
                <TableCell align="right" sx={{ borderLeft: (t) => `1px solid ${t.palette.outlineVariant}`, fontWeight: 600 }}>Итого</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              )}
              {!loading && preview.rows.length === 0 && addedItems.length === 0 && (
                <TableRow><TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>Нет данных по выбранным фильтрам</TableCell></TableRow>
              )}
              {!loading && renderRows(preview.rows, 0, 'root')}

              {addedItems.map((ai, idx) => {
                const qty = Number(ai.plan_qty) || 0;
                const revenue = qty * (Number(ai.avg_price) || 0);
                const profit = qty * (Number(ai.unit_profit) || 0);
                const totalVal = metric === 'revenue' ? revenue : metric === 'profit' ? profit : qty;
                const byDate = uniformByDate(totalVal);
                return (
                  <TableRow key={`added-${idx}`} sx={{ bgcolor: (t) => t.palette.action.hover }}>
                    <TableCell sx={{ pl: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 28 }} />
                        <Chip size="small" label="новая" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                        <Typography variant="body2">{ai.entity_key} <Typography component="span" variant="caption" color="text.secondary">({LEVEL_LABEL[ai.level]})</Typography></Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ borderLeft: (t) => `1px solid ${t.palette.outlineVariant}` }}>
                      <IconButton size="small" color="error" onClick={() => setAddedItems((prev) => prev.filter((_, i) => i !== idx))} sx={{ p: 0.25 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    {dates.map((d) => (
                      <TableCell key={d.key} align="right">{fmtMetric(byDate[d.key], metric)}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 600, borderLeft: (t) => `1px solid ${t.palette.outlineVariant}` }}>
                      {fmtMetric(totalVal, metric)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <AddItemDialog open={addOpen} onClose={() => setAddOpen(false)} onAdd={(item) => { setAddedItems((prev) => [...prev, item]); setAddOpen(false); }} canFinance={canFinance} />
    </>
  );
}

function AddItemDialog({ open, onClose, onAdd, canFinance }) {
  const [level, setLevel] = useState('article');
  const [entityKey, setEntityKey] = useState('');
  const [parentKey, setParentKey] = useState('');
  const [planQty, setPlanQty] = useState('');
  const [avgPrice, setAvgPrice] = useState('');
  const [unitProfit, setUnitProfit] = useState('');

  useEffect(() => {
    if (open) { setLevel('article'); setEntityKey(''); setParentKey(''); setPlanQty(''); setAvgPrice(''); setUnitProfit(''); }
  }, [open]);

  const submit = () => {
    if (!entityKey.trim()) { notify('Укажите название позиции', { severity: 'warning' }); return; }
    onAdd({
      level,
      entity_key: entityKey.trim(),
      parent_key: parentKey.trim() || null,
      plan_qty: Number(planQty) || 0,
      avg_price: Number(avgPrice) || 0,
      unit_profit: Number(unitProfit) || 0,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Добавить позицию вручную
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Уровень" value={level} onChange={(e) => setLevel(e.target.value)} size="small" fullWidth>
            <MenuItem value="gruppa">Группа</MenuItem>
            <MenuItem value="podgruppa">Подгруппа</MenuItem>
            <MenuItem value="article">Товар</MenuItem>
          </TextField>
          <TextField label="Название / артикул" value={entityKey} onChange={(e) => setEntityKey(e.target.value)} size="small" fullWidth />
          {level !== 'gruppa' && (
            <TextField label="Родитель (опционально)" value={parentKey} onChange={(e) => setParentKey(e.target.value)} size="small" fullWidth
              helperText={level === 'article' ? 'Название подгруппы' : 'Название группы'} />
          )}
          <TextField label="План, шт" type="number" value={planQty} onChange={(e) => setPlanQty(e.target.value)} size="small" fullWidth />
          {canFinance && (
            <>
              <TextField label="Средняя цена, ₽" type="number" value={avgPrice} onChange={(e) => setAvgPrice(e.target.value)} size="small" fullWidth />
              <TextField label="Профит за шт, ₽" type="number" value={unitProfit} onChange={(e) => setUnitProfit(e.target.value)} size="small" fullWidth />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={submit}>Добавить</Button>
      </DialogActions>
    </Dialog>
  );
}
